import { createEffect, mergeProps, untrack, Show } from "solid-js";
import { TextButton } from "../button/Button";
import { Dialog } from "@ark-ui/solid";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import { omit } from "lodash-es";
import { type GenericSchema } from "valibot";
import type { FormProps } from "../form/Form";
import { Form } from "../form/Form";
import { useCurrentModal } from "./ModalManager";
import { createForm } from "../form/createForm";
import Text from "../text/Text.tsx";

export type InputModalProps<T extends Record<string, unknown>> = {
  variant: "default" | "destructive";
  size?: "small" | "large";
  title: string;
  titleIcon?: IconName;
  description?: string;
  confirmLabel: string;
  confirmLabelSlow?: string;
  cancelLabel: string;
  onConfirm: ((value: T) => Promise<void>) | ((value: T) => void);
  onCancel?: () => void;
  schema: GenericSchema<unknown, T>;
  initialValue?: Partial<T>;
  closeOnSubmit?: boolean;
} & Pick<FormProps<T>, "children">;

const InputModal = <T extends Record<string, unknown>>(rawProps: InputModalProps<T>) => {
  const props = mergeProps({ size: "small", closeOnSubmit: true }, rawProps);
  const { close, changeCloseBehaviour } = useCurrentModal();
  const form = createForm(
    untrack(() => props.schema),
    async (value) => {
      await props.onConfirm(value);
      if (props.closeOnSubmit) {
        close();
      }
    },
    props.initialValue,
  );

  createEffect(() => {
    changeCloseBehaviour(form.isSubmitting() ? "off" : "default");
  });

  return (
    <div
      class="flex max-h-160 flex-col p-6 shadow-elevation-light-5"
      classList={{
        "w-86": props.size === "small",
        "w-120": props.size === "large",
      }}
    >
      <Dialog.Title class="mb-3 flex items-center gap-2">
        <div
          class="flex size-9 flex-none shrink-0 items-center justify-center rounded-full"
          classList={{
            "bg-surface-status-subtle-informative": props.variant === "default",
            "bg-surface-status-subtle-danger": props.variant === "destructive",
          }}
        >
          <Icon
            name={props.titleIcon ?? "pencilAlt"}
            size="xl"
            type="outline"
            class="stroke-2"
            classList={{
              "icon-status-informative": props.variant === "default",
              "icon-status-danger": props.variant === "destructive",
            }}
          />
        </div>
        <Text as="h2" text={props.title} truncate={2} class="text-header-lg text-primary" />
      </Dialog.Title>

      <Dialog.Description>
        <Show when={props.description}>
          {(description) => (
            <Text as="p" text={description()} truncate={4} class="mb-3 text-base-regular text-secondary" />
          )}
        </Show>
      </Dialog.Description>

      {/* Single px is needed for bottom focus outline */}
      <div class="-mx-6 overflow-y-scroll px-6 pt-3 pb-px">
        <Form form={form}>{props.children}</Form>
      </div>

      <div class="flex gap-2 pt-6" classList={{ "justify-end": props.size === "large" }}>
        <Dialog.CloseTrigger
          asChild={(parentProps) => (
            <TextButton
              {...omit(parentProps(), "children")}
              text={props.cancelLabel}
              disabled={form.isSubmitting()}
              variant="secondary"
              block={props.size === "small"}
              size="large"
            />
          )}
        />
        <TextButton
          text={form.isSubmittingSlow() ? (props.confirmLabelSlow ?? props.confirmLabel) : props.confirmLabel}
          disabled={form.submitDisabled()}
          loading={form.isSubmitting()}
          variant="primary"
          color={props.variant === "destructive" ? "destructive" : undefined}
          block={props.size === "small"}
          size="large"
          form={form.id}
          type="submit"
        />
      </div>
    </div>
  );
};

export default InputModal;
